export type CommunicationTagConfig = {
  label: string;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
};

export const COMMUNICATION_CHANNEL_COLORS: Record<string, string> = {
  email: 'var(--communication-channel-email)',
  call: 'var(--communication-channel-call)',
  voicemail: 'var(--communication-channel-voicemail)',
  sms: 'var(--communication-channel-sms)',
};

export const COMMUNICATION_CHANNEL_GRADIENTS: Record<string, string> = {
  email:
    'linear-gradient(to right, var(--communication-channel-email-gradient-start), var(--communication-channel-email))',
  call: 'linear-gradient(to right, var(--communication-channel-call-gradient-start), var(--communication-channel-call))',
  voicemail:
    'linear-gradient(to right, var(--communication-channel-voicemail-gradient-start), var(--communication-channel-voicemail))',
  sms: 'linear-gradient(to right, var(--communication-channel-sms-gradient-start), var(--communication-channel-sms))',
};

export const COMMUNICATION_CHANNEL_ORDER = ['email', 'call', 'voicemail', 'sms'];

export const COMMUNICATION_TAG_LABELS: Record<string, string> = {
  ace_insights: 'ACE Insights',
  answered: 'Answered',
  ambiguous: 'Ambiguous',
  audio_recording: 'Audio Recording',
  audio_transcription: 'Audio Transcription',
  call: 'Call',
  call_recording: 'Call Recording',
  dropped: 'Dropped',
  fax: 'Fax',
  fetched: 'Fetched',
  inbound: 'Inbound',
  ignored: 'Ignored',
  matched: 'Matched',
  mms_attachment: 'MMS Attachment',
  missed: 'Missed',
  no_answer: 'No Answer',
  outbound: 'Outbound',
  rendered_document: 'Rendered Document',
  sms: 'SMS',
  sent: 'Sent',
  received: 'Received',
  text: 'Text',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
  unmatched: 'Unmatched',
  hung_up: 'Hung Up',
  voicemail: 'Voicemail',
};

export const COMMUNICATION_RECORD_TAG_CONFIG: Record<string, CommunicationTagConfig> = {
  call: {
    label: COMMUNICATION_TAG_LABELS.call,
    textColor: '--positive-green-dark',
    backgroundColor: '--mint-pale',
    borderColor: '--communication-channel-call',
  },
  sms: {
    label: COMMUNICATION_TAG_LABELS.sms,
    textColor: '--communication-channel-sms-dark',
    backgroundColor: '--gold-pale',
    borderColor: '--communication-channel-sms',
  },
  voicemail: {
    label: COMMUNICATION_TAG_LABELS.voicemail,
    textColor: '--communication-channel-voicemail-dark',
    backgroundColor: '--pink-pale',
    borderColor: '--communication-channel-voicemail',
  },
  fax: {
    label: COMMUNICATION_TAG_LABELS.fax,
    textColor: '--communication-channel-sms-dark',
    backgroundColor: '--gold-pale',
    borderColor: '--amber-light',
  },
  answered: {
    label: COMMUNICATION_TAG_LABELS.answered,
    textColor: '--positive-green-dark',
    backgroundColor: '--positive-green-light',
    borderColor: '--positive-green-border-light',
  },
  missed: {
    label: COMMUNICATION_TAG_LABELS.missed,
    textColor: '--error-red-dark',
    backgroundColor: '--error-red-background',
    borderColor: '--error-red-border-light',
  },
  no_answer: {
    label: COMMUNICATION_TAG_LABELS.no_answer,
    textColor: '--error-red-dark',
    backgroundColor: '--error-red-background',
    borderColor: '--error-red-border-light',
  },
  dropped: {
    label: COMMUNICATION_TAG_LABELS.dropped,
    textColor: '--error-red-dark',
    backgroundColor: '--error-red-background',
    borderColor: '--error-red-border-light',
  },
  hung_up: {
    label: COMMUNICATION_TAG_LABELS.hung_up,
    textColor: '--error-red-dark',
    backgroundColor: '--error-red-background',
    borderColor: '--error-red-border-light',
  },
  received: {
    label: COMMUNICATION_TAG_LABELS.received,
    textColor: '--positive-green-dark',
    backgroundColor: '--positive-green-background',
    borderColor: '--positive-green-border-light',
  },
  sent: {
    label: COMMUNICATION_TAG_LABELS.sent,
    textColor: '--deep-blue',
    backgroundColor: '--blue-mist',
    borderColor: '--blue-sky',
  },
  inbound: {
    label: COMMUNICATION_TAG_LABELS.inbound,
    textColor: '--positive-green-dark',
    backgroundColor: '--positive-green-background',
    borderColor: '--positive-green-border-light',
  },
  outbound: {
    label: COMMUNICATION_TAG_LABELS.outbound,
    textColor: '--deep-blue',
    backgroundColor: '--blue-mist',
    borderColor: '--blue-sky',
  },
  matched: {
    label: COMMUNICATION_TAG_LABELS.matched,
    textColor: '--positive-green-dark',
    backgroundColor: '--positive-green-light',
    borderColor: '--positive-green-border-light',
  },
  unmatched: {
    label: COMMUNICATION_TAG_LABELS.unmatched,
    textColor: '--text-secondary',
    backgroundColor: '--lighter-gray',
    borderColor: '--line-gray',
  },
  ambiguous: {
    label: COMMUNICATION_TAG_LABELS.ambiguous,
    textColor: '--communication-channel-sms-dark',
    backgroundColor: '--pale-yellow',
    borderColor: '--warm-gold',
  },
  ignored: {
    label: COMMUNICATION_TAG_LABELS.ignored,
    textColor: '--error-red-dark',
    backgroundColor: '--error-red-background',
    borderColor: '--error-red-border-light',
  },
  unknown: {
    label: COMMUNICATION_TAG_LABELS.unknown,
    textColor: '--text-secondary',
    backgroundColor: '--background-gray-soft',
    borderColor: '--line-gray',
  },
};

export const getCommunicationTagConfig = (value?: string | null) => {
  if (!value) {
    return COMMUNICATION_RECORD_TAG_CONFIG.unknown;
  }

  return (
    COMMUNICATION_RECORD_TAG_CONFIG[value] ?? {
      label: COMMUNICATION_TAG_LABELS[value] ?? value,
      textColor: '--text-secondary',
      backgroundColor: '--background-gray-soft',
      borderColor: '--line-gray',
    }
  );
};

export const getCommunicationTagLabel = (value?: string | null) => {
  if (!value) {
    return COMMUNICATION_TAG_LABELS.unknown;
  }

  return COMMUNICATION_TAG_LABELS[value] ?? value;
};
